import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getEntities, getPositions, getOfficers } from '../api/officerAppointmentApi';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';

export interface Entity {
  // 백엔드에서 대문자로 반환하는 필드
  ENTITY_CD?: string;
  COMPANY_NAME?: string;
  REG_NO?: string;
  CLIENT_CD?: string;
  COMPANY_TYPE?: string;
  // camelCase 호환성
  entityCd?: string;
  companyName?: string;
  regNo?: string;
  clientCd?: string;
  companyType?: string;
}

export interface Position {
  totalCode: string;
  codeName: string;
  subCode: string;
  orderBy: number;
}

export interface Officer {
  contactCd: string;
  contactName: string;
  contactRegNo: string;
  contactEmail: string;
}

export const useOfficerAppointmentStore = defineStore('officerAppointment', () => {
  // Translation
  const languageStore = useLanguageStore();
  const { createSyncT } = useTranslation();
  const t = createSyncT(languageStore.currentLanguage);

  // State
  const entities = ref<Entity[]>([]);
  const positions = ref<Position[]>([]);
  const officers = ref<Officer[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Actions
  const fetchEntities = async () => {
    try {
      loading.value = true;
      error.value = null;
      const response = await getEntities();
      if (response.success && response.data) {
        entities.value = response.data.entities || [];
      } else {
        const errorMessage = typeof response.error === 'string' 
          ? response.error 
          : response.error?.message || (t('startTask.Failed to fetch entities') || 'Failed to fetch entities');
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      error.value = err.response?.data?.error || err.message || (t('startTask.Failed to fetch entities') || 'Failed to fetch entities');
      entities.value = [];
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchPositions = async (entityCd: string) => {
    try {
      loading.value = true;
      error.value = null;
      const response = await getPositions(entityCd);
      if (response.success && response.data) {
        positions.value = response.data.positions || [];
      } else {
        const errorMessage = typeof response.error === 'string' 
          ? response.error 
          : response.error?.message || (t('startTask.Failed to fetch positions') || 'Failed to fetch positions');
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      error.value = err.response?.data?.error || err.message || (t('startTask.Failed to fetch positions') || 'Failed to fetch positions');
      positions.value = [];
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchOfficers = async () => {
    try {
      loading.value = true;
      error.value = null;
      const response = await getOfficers();
      if (response.success && response.data) {
        officers.value = response.data.officers || [];
      } else {
        const errorMessage = typeof response.error === 'string' 
          ? response.error 
          : response.error?.message || (t('startTask.Failed to fetch officers') || 'Failed to fetch officers');
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      error.value = err.response?.data?.error || err.message || (t('startTask.Failed to fetch officers') || 'Failed to fetch officers');
      officers.value = [];
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Computed
  // 백엔드에서 camelCase로 변환하여 반환하므로 companyName, entityCd 사용
  // UPPERCASE도 fallback으로 지원 (이전 버전 호환성)
  const entityOptions = computed(() => {
    return entities.value
      .filter((entity: any) => {
        const name = entity.companyName || entity.COMPANY_NAME;
        const cd = entity.entityCd || entity.ENTITY_CD;
        return name && cd; // undefined 필터링
      })
      .map((entity: any) => ({
        title: entity.companyName || entity.COMPANY_NAME || '',
        value: entity.entityCd || entity.ENTITY_CD || ''
      }));
  });

  const positionOptions = computed(() => {
    return positions.value
      .sort((a, b) => a.orderBy - b.orderBy)
      .map((position) => ({
        title: position.codeName,
        value: position.totalCode
      }));
  });

  const officerOptions = computed(() => {
    return officers.value.map((officer) => ({
      title: officer.contactName,
      value: officer.contactCd
    }));
  });

  return {
    // State
    entities,
    positions,
    officers,
    loading,
    error,
    // Actions
    fetchEntities,
    fetchPositions,
    fetchOfficers,
    // Computed
    entityOptions,
    positionOptions,
    officerOptions
  };
});

